import { useCallback, useEffect, useRef, useState } from 'react'
import type { ImpersonationEnd } from '@/contexts/auth/impersonation'
import { useAuth } from '@/hooks/use-auth'
import { useRouter } from '@/hooks/use-router'
import { watchImpersonationRefusal } from '@/lib/watchImpersonationRefusal'
import { paths } from '@/paths'

const TICK_MS = 15_000
/** Au-delà, `setTimeout` part tout de suite. */
const MAX_TIMEOUT_MS = 2 ** 31 - 1

/**
 * La session « en tant que » vue de l'écran : temps restant, fermeture à l'échéance ou au refus de la
 * gateway, puis l'accueil de sa propre session, ou la connexion s'il n'y en avait pas.
 */
export const useImpersonationSession = (): {
  minutesLeft: number | null
  end: () => void
} => {
  const { isImpersonating, impersonationExpiresAt, stopImpersonation } =
    useAuth()
  const router = useRouter()
  const [now, setNow] = useState(() => Date.now())
  const closing = useRef(false)

  const close = useCallback(
    async (reason: ImpersonationEnd): Promise<void> => {
      if (closing.current) return
      closing.current = true
      const restored = await stopImpersonation(reason)
      // Une session reprise sur le même membre ne remonte pas l'écran : elle doit pouvoir se fermer à son tour.
      if (restored) closing.current = false
      router.replace(restored ? paths.index : paths.auth.jwt.login)
    },
    [router, stopImpersonation]
  )

  useEffect(() => {
    if (isImpersonating) closing.current = false
  }, [isImpersonating])

  useEffect(() => {
    if (!isImpersonating) return
    return watchImpersonationRefusal(() => void close('closed'))
  }, [isImpersonating, close])

  useEffect(() => {
    if (!isImpersonating || impersonationExpiresAt === null) return
    setNow(Date.now())
    const tick = setInterval(() => setNow(Date.now()), TICK_MS)
    const expiry = setTimeout(
      () => void close('expired'),
      Math.min(Math.max(0, impersonationExpiresAt - Date.now()), MAX_TIMEOUT_MS)
    )
    return () => {
      clearInterval(tick)
      clearTimeout(expiry)
    }
  }, [isImpersonating, impersonationExpiresAt, close])

  return {
    minutesLeft:
      isImpersonating && impersonationExpiresAt !== null
        ? Math.max(0, Math.ceil((impersonationExpiresAt - now) / 60_000))
        : null,
    end: () => void close('ended'),
  }
}
